import { manuscriptSelectionText } from "../../lib/manuscriptSelectionText";
import * as Sentry from "@sentry/react";
import { dismissImportConversationStarters } from "./dismissImportConversationStarters";
import { DOMSerializer } from "prosemirror-model";
import { Editor } from "@tiptap/core";
import { debounce } from "lodash";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useProjectStore } from "src/stores/projectStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { isCommentingKeepingChatTurnOpen } from "./commentingTurnState";
import { useCommentStore } from "src/stores/commentStore";
import { Text } from "src/sudokit/components/Text";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { PersistentChatWithMessages } from "~shared/types/persistent-chat-types";
import { fetchChatPage, mergeOlderChatMessages, useCurrentChat } from "./PersistentChatQueries";
import { chatQueryKey, DefaultChatModel, NEW_CHAT_ID } from "./PersistentChatTypes";
import { useOnSudoLayoutEvent } from "src/lib/layout-events";
import { useQueryClient } from "@tanstack/react-query";
import { useCrossAppSignalStore, getChapterCTASignal, getTraitCTASignal, getWorldbuildingTraitCTASignal, getSynopsisCTASignal } from "src/stores/crossAppSignalStore";
import { handleStoryBibleSelectionChange, isFocusInsideAgenticComposer } from "./storyBibleSelection";
import { getEffectiveSelection } from "src/lib/tipTapExtensions/persistedSelection";
import { publishStoryBibleGenerateForCTA } from "./ctaAnalytics";
import type { CTAActionType } from "./ThinkingStep/cta-types";
import { isStoryBibleCtaType } from "~shared/agenticChat/storyBibleCtaTypes";
import { isStoryBibleCtaBlockingRetrigger } from "./storyBibleCta";
import { promoteComposerToAllowEditsForStoryBibleCta } from "./storyBibleCtaModeSwitch";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { buildEditorSelectionHintForPassage, passageMatchesDocRangeText } from "src/utils/tiptapSuggestion";
import { getActiveManuscriptCommentThreadIdForAgent, getCommentSidebarOrderForAgent } from "src/lib/activeManuscriptCommentForAgent";
import { collectAnnotationIdsFromTipTapSelection } from "src/lib/tiptap-selection-annotation-ids";
import type { EditorSelectionHint, PlanData } from "src/stores/persistentChatStore";
import { linearMessagesForDisplay, linearPathToLeaf } from "~shared/persistentChatBranching";
import {
  appendStoryBibleCtaSeededUserMessage,
  CTA_SEED_USER_MESSAGE_ID_PREFIX,
  resolveEffectiveChatId,
  resolveStoryBibleCtaTargetChatId,
} from "./persistentChatSwitchPolicy";
import { appendSidebarTreeMentions } from "./appendSidebarTreeMentions";
import { useProjectTreeSelectionStore } from "src/stores/projectTreeSelectionStore";
import { buildSidebarMentionNodeIds } from "./selectionTargets";
import { buildEditorPlainText, plainTextRangeForPmSelection } from "~shared/editorPlainText";

/** Chip replay may store plain-text offsets when PM bounds are unavailable cross-document. */
function chipStoredSelectionHintIsUsable(hint: EditorSelectionHint | null | undefined): boolean {
  if (!hint) return false;
  const pmFrom = Number(hint.pmFrom);
  const pmTo = Number(hint.pmTo);
  if (Number.isFinite(pmFrom) && Number.isFinite(pmTo) && pmTo > pmFrom) return true;
  const plainTextStart = Number(hint.plainTextStart);
  const plainTextEnd = Number(hint.plainTextEnd);
  if (Number.isFinite(plainTextStart) && Number.isFinite(plainTextEnd) && plainTextEnd > plainTextStart) {
    return true;
  }
  return Number.isFinite(plainTextStart);
}

export type { AgenticToolRun, AgenticOutlineUpdate } from "src/stores/persistentChatStore";

type PersistentChatContextType = PersistentChatWithMessages & {
  /** Full message graph from the server (all branches); use with branching helpers. */
  branchRawMessages: PersistentChatWithMessages["messages"];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  isFetching: boolean;

  chatId: string;
  setChatId: (chatId: string) => void;

  projectId: string;
  isStoryBibleActive: boolean;

  getSelection: () => string;
  editor: Editor;
  dismissActiveHighlightFromComposer: () => void;

  sendMessage: (
    message: string,
    modelKey?: string,
    mode?: "write" | "chat",
    hidden?: boolean,
    onStreamError?: (message: string) => void,
    options?: {
      ctaAction?: {
        type: string;
        targetSurface?: "story_bible_outline_summary";
        targetId?: string;
        targetName?: string;
        customInstructions?: string;
        outlineFramework?: string;
        characterId?: string;
        entryId?: string;
        traitId?: string;
        traitLabel?: string;
        role?: string;
        roleLabel?: string;
        roleExplicitlySelected?: boolean;
        fields?: Array<{ id?: string; label: string; value?: unknown }>;
      };
      resendOfMessageId?: string;
      resendIntent?: "retry" | "edit";
      keepProposedPlan?: boolean;
      editedPlanApproved?: boolean;
      editedPlan?: PlanData;
      userMessageMetadata?: Record<string, unknown>;
      pastedExcerpts?: string[];
      instructionsDisplayOverride?: string;
      passageOverride?: string;
      editorSelectionHintOverride?: {
        documentId: string;
        pmFrom?: number;
        pmTo?: number;
        plainTextStart?: number;
        plainTextEnd?: number;
      };
    },
  ) => Promise<void>;

  hasMoreOlderMessages: boolean;
  isLoadingOlderMessages: boolean;
  loadOlderMessages: () => Promise<void>;
};

export type AttachmentDoc = {
  type: "paste";
  content: string;
  wordCount: number;
  id: string;
};

const PersistentChatContext = createContext<PersistentChatContextType | null>(null);

function getLiveManuscriptSelectionText(editor: Editor): string {
  const { from, to, empty } = editor.state.selection;
  if (empty || to <= from) return "";
  return manuscriptSelectionText(editor.state.doc, from, to).trim();
}

function buildEditorSelectionHintFromEffective(
  editor: Editor,
  documentId: string,
  effective: ReturnType<typeof getEffectiveSelection>,
): EditorSelectionHint | undefined {
  if (!effective || effective.to <= effective.from) return undefined;
  const doc = editor.state.doc;
  const plainRange = plainTextRangeForPmSelection(doc, effective.from, effective.to);
  return {
    documentId,
    pmFrom: effective.from,
    pmTo: effective.to,
    plainTextStart: plainRange.start,
    plainTextEnd: plainRange.end,
  };
}

function getSelectionHtml(editor: Editor | undefined): string {
  if (!editor || typeof document === "undefined") return "";

  // The persisted (fake) selection highlight is the selection of record when
  // the editor is blurred; fall back to the live selection otherwise.
  const effective = getEffectiveSelection(editor);
  if (!effective) return "";

  try {
    const serializer = DOMSerializer.fromSchema(editor.schema);
    const container = document.createElement("div");
    const slice = editor.state.doc.slice(effective.from, effective.to);
    const fragment = serializer.serializeFragment(slice.content, { document });
    container.appendChild(fragment);
    return container.innerHTML.trim();
  } catch {
    return "";
  }
}

function collectExactSidebarOrderIdsFromStore(): string[] | undefined {
  const { comments } = useCommentStore.getState();
  if (!Array.isArray(comments) || comments.length === 0) return undefined;

  // commentStore.setComments already applies sortCommentsByDomPosition; reuse that order for the agent.
  const ids = comments.map((c) => String(c?.id ?? "").trim()).filter(Boolean);
  return ids.length > 0 ? ids : undefined;
}

type Props = {
  children: React.ReactNode;
  chatId: string;
  setChatId: (chatId: string) => void;
  editor: Editor;
  /** Firebase project that owns the active chat (may differ from the book being viewed). */
  chatProjectId: string;
};

export const PersistentChatProvider: React.FC<Props> = ({ children, chatId, setChatId, editor, chatProjectId }) => {
  const viewingProjectId = useProjectStore((state) => state.project)?.id as string;
  const projectId = chatProjectId;
  const ctaTargetRef = useRef<{ projectId: string; chatId: string } | null>(null);
  const queryClient = useQueryClient();
  const pendingNewChatMaterializationId = usePersistentChatStore((s) => s.pendingNewChatMaterializationId);
  const queryChatId = resolveEffectiveChatId(chatId, pendingNewChatMaterializationId);
  const { data, isLoading, isError, refetch, isFetching } = useCurrentChat(projectId, queryChatId);
  const isStoryBibleActive = useStoryBibleStore((state) => state.isActive);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const isLoadingOlderMessagesRef = useRef(isLoadingOlderMessages);
  isLoadingOlderMessagesRef.current = isLoadingOlderMessages;

  const streaming = usePersistentChatStore((s) => s.streaming);
  const pendingAgentResponse = usePersistentChatStore((s) => s.pendingAgentResponse);
  const activeCommenting = usePersistentChatStore((s) => s.activeCommenting);
  const currentTurnUserMessageId = usePersistentChatStore((s) => s.currentTurnUserMessageId);
  const optimisticUserMessageId = usePersistentChatStore((s) => s.optimisticUserMessageId);
  const inFlightUserMessageId =
    streaming || pendingAgentResponse || isCommentingKeepingChatTurnOpen(activeCommenting)
      ? optimisticUserMessageId ?? currentTurnUserMessageId
      : null;
  const currentStreamingText = usePersistentChatStore((s) => s.currentStreamingText);
  const setActiveHighlight = usePersistentChatStore((s) => s.setActiveHighlight);
  const dismissActiveHighlight = usePersistentChatStore((s) => s.dismissActiveHighlight);
  const clearDismissedHighlight = usePersistentChatStore((s) => s.clearDismissedHighlight);
  const sendMessage = usePersistentChatStore((s) => s.sendMessage);
  const clearStreamingTextIfServerHasData = usePersistentChatStore((s) => s.clearStreamingTextIfServerHasData);

  const getSelection = useCallback(() => {
    if (!editor) return "";

    const tiptapEditor = (editor as unknown as { tiptap: Editor })?.tiptap;
    if (!tiptapEditor) return "";

    // While the manuscript editor is focused, trust the live PM range over any
    // persisted fake highlight so chip/send state tracks selection changes.
    if (tiptapEditor.isFocused) {
      const liveSelection = getLiveManuscriptSelectionText(tiptapEditor);
      if (liveSelection) return liveSelection;
    }

    // Persisted (fake) highlight wins over the live selection so the chip and
    // send flow keep working while focus is in the chat composer.
    const effective = getEffectiveSelection(tiptapEditor);
    return effective?.text?.trim() || "";
  }, [editor]);

  const dismissActiveHighlightFromComposer = useCallback(() => {
    const tiptapEditor = (editor as unknown as { tiptap: Editor })?.tiptap;
    tiptapEditor?.commands.clearPersistedSelection?.();
    dismissActiveHighlight();
  }, [dismissActiveHighlight, editor]);

  useEffect(() => {
    const branchRaw = data?.messages || [];
    // Reconcile local streaming state only after the server's canonical branch has advanced.
    // The UI may temporarily extend that branch with the in-flight user turn, but clearing
    // against that temporary path can create a blank frame before the persisted reply appears.
    const messages = linearMessagesForDisplay(branchRaw as any, data?.active_leaf_message_id);
    clearStreamingTextIfServerHasData(messages as any, projectId);
  }, [
    data?.messages,
    data?.active_leaf_message_id,
    streaming,
    currentStreamingText,
    clearStreamingTextIfServerHasData,
    projectId,
  ]);

  useEffect(() => {
    if (!editor) return;

    const tiptapEditor = (editor as unknown as { tiptap: Editor })?.tiptap;
    if (!tiptapEditor) return;

    let skipBlurHighlightClearDueToComposerPointer = false;

    const onPointerDownCapture = (e: Event) => {
      const raw = e.target;
      const el =
        raw instanceof Element ? raw : raw instanceof Node ? (raw.parentElement as Element | null) : null;
      // Track the current gesture only: non-composer clicks must clear any stale true from a prior
      // pointerdown so a later manuscript blur still syncs/clears the highlight.
      skipBlurHighlightClearDueToComposerPointer = !!el?.closest("[data-agentic-chat-composer='true']");
    };

    document.addEventListener("pointerdown", onPointerDownCapture, true);

    const syncHighlightFromManuscriptSelection = (options?: { preserveWhenEmpty?: boolean }) => {
      const currentSelection = getSelection();
      const documentId = useProjectStore.getState().document?.id;
      const effective = getEffectiveSelection(tiptapEditor);
      const selectionHint =
        documentId
          ? buildEditorSelectionHintFromEffective(tiptapEditor, documentId, effective)
          : undefined;
      if (currentSelection && currentSelection.trim()) {
        const selectionHtml = getSelectionHtml(tiptapEditor);
        setActiveHighlight(currentSelection, selectionHtml, selectionHint ?? null);
      } else if (!isStoryBibleActive) {
        // Don't wipe the highlight if the user has just clicked into the chat composer —
        // the persisted (fake) selection highlight covers the blurred editor, but keep
        // this guard for interaction orderings where neither selection is readable yet.
        if (options?.preserveWhenEmpty || isFocusInsideAgenticComposer()) return;
        if (!usePersistentChatStore.getState().activeHighlight?.trim()) return;
        setActiveHighlight("");
      } else {
        clearDismissedHighlight();
      }
    };

    // Drag-select fires selectionUpdate on every pointer move; debounce chip updates so the
    // right sidebar does not re-render on each intermediate range. Flush on gesture end.
    const debouncedSyncHighlightFromManuscriptSelection = debounce(
      (options?: { preserveWhenEmpty?: boolean }) => {
        syncHighlightFromManuscriptSelection(options);
      },
      100,
    );

    const onManuscriptSelectionUpdate = () => {
      const currentSelection = getSelection();
      const previousHighlight = usePersistentChatStore.getState().activeHighlight;
      if (currentSelection && currentSelection !== previousHighlight) {
        clearDismissedHighlight();
      }
      // Refocusing the manuscript clears the persisted fake selection before the
      // live PM range is readable again; preserve the chip/hint until the user
      // makes a new selection or explicitly dismisses it.
      const preserveWhenEmpty = Boolean(usePersistentChatStore.getState().activeHighlight?.trim());
      debouncedSyncHighlightFromManuscriptSelection({ preserveWhenEmpty });
    };

    const flushManuscriptHighlightSync = () => {
      debouncedSyncHighlightFromManuscriptSelection.flush();
    };

    // Defer to a macrotask so focus moves (e.g. manuscript blur → composer) settle before syncing.
    const handleBlurSync = () => {
      debouncedSyncHighlightFromManuscriptSelection.cancel();
      setTimeout(() => {
        const preserveWhenEmpty =
          isFocusInsideAgenticComposer() || skipBlurHighlightClearDueToComposerPointer;
        skipBlurHighlightClearDueToComposerPointer = false;
        syncHighlightFromManuscriptSelection({ preserveWhenEmpty });
      }, 0);
    };

    // Re-read selection after TipTap regains focus (mirrors Legacy); microtask waits for editor state.
    const handleFocusSync = () => {
      debouncedSyncHighlightFromManuscriptSelection.cancel();
      queueMicrotask(() => {
        const preserveWhenEmpty = Boolean(usePersistentChatStore.getState().activeHighlight?.trim());
        syncHighlightFromManuscriptSelection({ preserveWhenEmpty });
      });
    };

    tiptapEditor.on("selectionUpdate", onManuscriptSelectionUpdate);
    tiptapEditor.on("blur", handleBlurSync);
    tiptapEditor.on("focus", handleFocusSync);
    // pointerup covers mouse/touch drag-select; keyup covers shift+arrow selection.
    document.addEventListener("pointerup", flushManuscriptHighlightSync);
    document.addEventListener("keyup", flushManuscriptHighlightSync);

    return () => {
      document.removeEventListener("pointerdown", onPointerDownCapture, true);
      document.removeEventListener("pointerup", flushManuscriptHighlightSync);
      document.removeEventListener("keyup", flushManuscriptHighlightSync);
      debouncedSyncHighlightFromManuscriptSelection.cancel();
      tiptapEditor.off("selectionUpdate", onManuscriptSelectionUpdate);
      tiptapEditor.off("blur", handleBlurSync);
      tiptapEditor.off("focus", handleFocusSync);
    };
  }, [editor, getSelection, setActiveHighlight, clearDismissedHighlight, isStoryBibleActive]);

  const debouncedStoryBibleSelectionChange = useMemo(
    () =>
      debounce(() => {
        handleStoryBibleSelectionChange(setActiveHighlight, clearDismissedHighlight, getSelection);
      }, 100),
    [clearDismissedHighlight, getSelection, setActiveHighlight],
  );

  useEffect(() => {
    if (!isStoryBibleActive) return;

    document.addEventListener("selectionchange", debouncedStoryBibleSelectionChange);
    return () => {
      debouncedStoryBibleSelectionChange.cancel();
      document.removeEventListener("selectionchange", debouncedStoryBibleSelectionChange);
    };
  }, [debouncedStoryBibleSelectionChange, isStoryBibleActive]);

  const handleSendMessage = useCallback(
    async (
      instructions: string,
      modelKey?: string,
      mode?: "write" | "chat",
      hidden?: boolean,
      onStreamError?: (message: string) => void,
      options?: {
        ctaAction?: {
          type: string;
          targetSurface?: "story_bible_outline_summary";
          targetId?: string;
          targetName?: string;
          customInstructions?: string;
          outlineFramework?: string;
          characterId?: string;
          entryId?: string;
          traitId?: string;
          traitLabel?: string;
          role?: string;
          roleLabel?: string;
          roleExplicitlySelected?: boolean;
          fields?: Array<{ id?: string; label: string; value?: unknown }>;
        };
        resendOfMessageId?: string;
        resendIntent?: "retry" | "edit";
        parentMessageId?: string | null;
        keepProposedPlan?: boolean;
        editedPlanApproved?: boolean;
        editedPlan?: PlanData;
        userMessageMetadata?: Record<string, unknown>;
        pastedExcerpts?: string[];
        instructionsDisplayOverride?: string;
        passageOverride?: string;
        editorSelectionHintOverride?: {
          documentId: string;
          pmFrom?: number;
          pmTo?: number;
          plainTextStart?: number;
          plainTextEnd?: number;
        };
      },
    ) => {
      const ctaTarget = options?.ctaAction ? ctaTargetRef.current : null;
      if (ctaTarget && useProjectStore.getState().project?.id !== ctaTarget.projectId) return;
      const projectId = ctaTarget?.projectId ?? chatProjectId;
      const queryChatId = resolveEffectiveChatId(ctaTarget?.chatId ?? chatId, usePersistentChatStore.getState().pendingNewChatMaterializationId);
      if (usePersistentChatStore.getState().queuedSend && options?.resendIntent !== "edit") return;
      const { activeHighlight, activeHighlightHtml, activeHighlightSelectionHint, dismissedHighlight } =
        usePersistentChatStore.getState();
      const overridePassage = typeof options?.passageOverride === "string"
        ? options.passageOverride.trim()
        : "";
      const openDocumentId = useProjectStore.getState().document?.id;
      const fullEditor = (editor as unknown as { tiptap: Editor })?.tiptap;
      const hasDismissedActiveHighlight = !activeHighlight?.trim() && !!dismissedHighlight?.trim();
      const storedHighlight = activeHighlight?.trim() ?? "";
      const liveSelection = hasDismissedActiveHighlight ? "" : getSelection().trim();
      const storedHighlightDocumentId =
        typeof activeHighlightSelectionHint?.documentId === "string"
          ? activeHighlightSelectionHint.documentId.trim()
          : "";
      const effectiveSelection = fullEditor ? getEffectiveSelection(fullEditor) : null;
      const liveSelectionIsInDifferentDocument = Boolean(
        (
          (
            fullEditor?.isFocused
            && fullEditor.state.selection.to > fullEditor.state.selection.from
          )
          || (effectiveSelection && effectiveSelection.to > effectiveSelection.from)
        )
        && openDocumentId
        && storedHighlightDocumentId
        && openDocumentId !== storedHighlightDocumentId,
      );
      const liveSelectionReplacesStoredHighlight = Boolean(
        liveSelection
        && storedHighlight
        && (
          !passageMatchesDocRangeText(liveSelection, storedHighlight)
          || liveSelectionIsInDifferentDocument
        ),
      );
      const passage = overridePassage
        || (hasDismissedActiveHighlight
          ? ""
          : (liveSelectionReplacesStoredHighlight ? liveSelection : (storedHighlight || liveSelection)));
      const liveSelectionHtml = getSelectionHtml(fullEditor);
      const passageHtml =
        hasDismissedActiveHighlight
          ? ""
          : passage.trim() === storedHighlight
            ? (activeHighlightHtml || liveSelectionHtml)
            : liveSelectionHtml;
      const passageTrimmedForChip = passage.trim();
      const highlightFromChip =
        !hasDismissedActiveHighlight
        && Boolean(storedHighlight)
        && passageTrimmedForChip === storedHighlight;
      const chipDocumentId =
        highlightFromChip
        && !liveSelectionIsInDifferentDocument
        && typeof activeHighlightSelectionHint?.documentId === "string"
        && chipStoredSelectionHintIsUsable(activeHighlightSelectionHint)
        && (
          !isStoryBibleActive
          || activeHighlightSelectionHint.documentId.trim() === openDocumentId
        )
          ? activeHighlightSelectionHint.documentId.trim()
          : "";
      const documentId = chipDocumentId || openDocumentId;
      const isCrossDocumentChip =
        Boolean(chipDocumentId)
        && Boolean(openDocumentId)
        && chipDocumentId !== openDocumentId;
      const documentText = isCrossDocumentChip
        ? ""
        : (fullEditor?.state?.doc ? buildEditorPlainText(fullEditor.state.doc) : "");
      const activeManuscriptCommentId = isCrossDocumentChip
        ? undefined
        : getActiveManuscriptCommentThreadIdForAgent() ?? undefined;
      const commentSidebarOrderIds = isCrossDocumentChip
        ? getCommentSidebarOrderForAgent(documentId)
        : collectExactSidebarOrderIdsFromStore()
          ?? getCommentSidebarOrderForAgent(documentId);

      let editorSelectionHint: EditorSelectionHint | undefined;
      if (!hasDismissedActiveHighlight && documentId && fullEditor) {
        try {
          const passageTrimmed = passageTrimmedForChip;
          const doc = fullEditor.state.doc;
          // The persisted (fake) highlight is the selection of record while the
          // composer has focus; fall back to the live PM selection.
          const effectiveSel = getEffectiveSelection(fullEditor);
          const focusedSel = fullEditor.state.selection;
          const focusedHasLiveRange = fullEditor.isFocused && focusedSel.to > focusedSel.from;
          const pmFrom = focusedHasLiveRange
            ? focusedSel.from
            : (effectiveSel?.from ?? focusedSel.from);
          const pmTo = focusedHasLiveRange
            ? focusedSel.to
            : (effectiveSel?.to ?? focusedSel.to);
          const { start: plainTextStart, end: plainTextEnd } = plainTextRangeForPmSelection(doc, pmFrom, pmTo);
          const liveHint =
            pmTo > pmFrom
              ? { documentId: openDocumentId ?? documentId, pmFrom, pmTo, plainTextStart, plainTextEnd }
              : undefined;
          const chipSelectionHint =
            highlightFromChip
            && chipDocumentId
            && activeHighlightSelectionHint
            && chipStoredSelectionHintIsUsable(activeHighlightSelectionHint)
              ? activeHighlightSelectionHint
              : undefined;
          const anchorHint =
            chipDocumentId && openDocumentId && chipDocumentId !== openDocumentId
              ? chipSelectionHint
              : liveHint ?? chipSelectionHint;

          if (
            chipDocumentId
            && openDocumentId
            && chipDocumentId !== openDocumentId
            && chipSelectionHint
          ) {
            editorSelectionHint = chipSelectionHint;
          } else if (passageTrimmed) {
            const searchedHint = buildEditorSelectionHintForPassage(fullEditor, {
              documentId,
              passage: passageTrimmed,
              preferredHint: anchorHint,
            });
            const liveRangeText = liveHint
              ? manuscriptSelectionText(doc, liveHint.pmFrom, liveHint.pmTo)
              : "";
            const liveSelectionMatchesPassage = Boolean(
              liveHint && passageMatchesDocRangeText(liveRangeText, passageTrimmed),
            );
            const searchedHintFromLiveSelection = Boolean(
              anchorHint
              && searchedHint
              && searchedHint.pmFrom === anchorHint.pmFrom
              && searchedHint.pmTo === anchorHint.pmTo,
            );
            if (
              highlightFromChip
              && searchedHint
              && !liveSelectionMatchesPassage
              && !searchedHintFromLiveSelection
            ) {
              // Duplicate passages: trust PM bounds from the highlight chip, not the first doc match.
              editorSelectionHint = anchorHint ?? undefined;
            } else if (liveHint && liveSelectionMatchesPassage) {
              // Prefer live PM bounds over a document search that may snap to an earlier duplicate.
              editorSelectionHint = liveHint;
            } else if (highlightFromChip && chipSelectionHint) {
              editorSelectionHint = chipSelectionHint;
            } else {
              editorSelectionHint = searchedHint ?? chipSelectionHint;
            }
          } else if (liveHint) {
            editorSelectionHint = liveHint;
          }
        } catch {
          editorSelectionHint = undefined;
        }
      }
      const overrideHint = options?.editorSelectionHintOverride;
      if (
        overrideHint
        && typeof overrideHint.documentId === "string"
        && overrideHint.documentId.trim().length > 0
      ) {
        editorSelectionHint = overrideHint;
      }

      let selectionAnnotationIds: string[] | undefined;
      if (!isCrossDocumentChip && fullEditor && passage?.trim()) {
        try {
          const effectiveRange = getEffectiveSelection(fullEditor);
          const ids = collectAnnotationIdsFromTipTapSelection(fullEditor, effectiveRange);
          if (ids.length > 0) selectionAnnotationIds = ids;
        } catch {
          // Selection/editor state may be unavailable during rapid updates
        }
      }

      const branchRaw = data?.messages ?? [];
      const activeLeaf = data?.active_leaf_message_id ?? null;
      const currentMessages = linearPathToLeaf(branchRaw as any, activeLeaf);

      let instructionsForAgent = instructions;
      // Open-tab "this chapter" deixis is bound server-side via openDocumentId / Current Document
      // context — do not auto-append #[Chapter](document::…) here. That fake explicit tag
      // outranks paste/highlight grounding and fights same-turn pasted prose analysis.
      const shouldAppendSidebarTreeMentions =
        !hidden
        && !options?.ctaAction
        && !options?.resendOfMessageId;
      if (shouldAppendSidebarTreeMentions) {
        const projectTreeSelectionState = useProjectTreeSelectionStore.getState();
        const projectState = useProjectStore.getState();
        const viewedProjectId = projectState.project?.id ?? null;
        const currentDocumentId = projectState.document?.id ?? null;
        const effectiveSelectedTreeNodeIds = buildSidebarMentionNodeIds({
          selectedTreeNodeIds: projectTreeSelectionState.selectedNodeIds,
          selectionProjectId: projectTreeSelectionState.projectId,
          viewedProjectId,
          chatProjectId: projectId,
          currentDocumentId,
        });
        const projectContents = projectState.contents ?? [];
        const projectFolders = projectState.project?.folders ?? [];
        const nodesById = Object.fromEntries(
          (projectState.project?.getAllTreeNodes?.() ?? []).map((node: { id: string }) => [node.id, node]),
        );
        const sidebarMentions = appendSidebarTreeMentions(
          instructions,
          effectiveSelectedTreeNodeIds,
          nodesById,
          projectContents,
          projectFolders,
        );
        if (sidebarMentions.hasAppendedTreeMentions) {
          instructionsForAgent = sidebarMentions.text;
        }
      }

      if (
        !overridePassage
        && !hasDismissedActiveHighlight
        && passage.trim()
        && passage.trim() !== storedHighlight
      ) {
        setActiveHighlight(passage, passageHtml, editorSelectionHint ?? null);
      }

      const viewedProject = useProjectStore.getState().project;
      if (!hidden && viewedProject?.id === projectId) {
        void dismissImportConversationStarters(viewedProject);
      }

      // Typed chat requests are classified on the server. Only explicit UI
      // actions may supply action metadata; text matching cannot grant consent.
      await sendMessage({
        instructions: instructionsForAgent,
        modelKey,
        mode,
        passage,
        passageHtml,
        selectionAnnotationIds,
        commentSidebarOrderIds,
        documentText,
        documentId,
        openDocumentId,
        activeManuscriptCommentId,
        projectId,
        chatId: queryChatId,
        isStoryBibleActive,
        setChatId,
        currentMessages: (ctaTarget && (ctaTarget.projectId !== chatProjectId || ctaTarget.chatId !== chatId) ? [] : currentMessages) as any,
        hidden,
        onStreamError,
        userMessageMetadata: options?.userMessageMetadata,
        pastedExcerpts: options?.pastedExcerpts,
        ctaAction: options?.ctaAction,
        resendOfMessageId: options?.resendOfMessageId,
        resendIntent: options?.resendIntent,
        parentMessageId: options?.parentMessageId,
        editorSelectionHint,
        keepProposedPlan: options?.keepProposedPlan,
        editedPlanApproved: options?.editedPlanApproved,
        editedPlan: options?.editedPlan,
        instructionsDisplayOverride: options?.instructionsDisplayOverride,
      });
    },
    [chatId, chatProjectId, queryChatId, data?.messages, data?.active_leaf_message_id, editor, getSelection, isStoryBibleActive, projectId, sendMessage, setChatId]
  );

  useOnSudoLayoutEvent(
    "send_introductory_chat_messages",
    async () => {
      setChatId(NEW_CHAT_ID);
      try {
        await handleSendMessage("I'd like to chat about an idea");
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    { deps: [handleSendMessage, setChatId] }
  );

  const ctaQueryClient = useQueryClient();

  useOnSudoLayoutEvent(
    "trigger_cta_chat",
    ({ payload }) => {
      const project = useProjectStore.getState().project;
      const importWip = project?.importStep && project.importStep !== "CANCELLED";
      if (!importWip) {
        useProjectStore.getState().setRightBarCollapsed(false);
      }

      const existingCta = usePersistentChatStore.getState().ctaAction;
      const isGenerateCastRetrigger =
        payload.type === "generate_cast" && existingCta?.type === "generate_cast";

      if (!isGenerateCastRetrigger && isStoryBibleCtaBlockingRetrigger(existingCta)) {
        return;
      }

      const targetProjectId = payload.projectId ?? viewingProjectId;
      const startTriggeredCtaChat = () => {
        if (useProjectStore.getState().project?.id !== targetProjectId) return;
        if (existingCta?.signalKey) {
          useCrossAppSignalStore.getState().setSignalActive(existingCta.signalKey, false);
        }

        usePersistentChatStore.getState().reset();

        if (isStoryBibleCtaType(payload.type)) {
          promoteComposerToAllowEditsForStoryBibleCta({
            userData: useSudowriteStore.getState().userData,
          });
        }

        const pendingNewChatMaterializationId = usePersistentChatStore.getState().pendingNewChatMaterializationId;
        const targetChatId = projectId === targetProjectId
          ? resolveStoryBibleCtaTargetChatId(chatId, pendingNewChatMaterializationId)
          : NEW_CHAT_ID;
        ctaTargetRef.current = { projectId: targetProjectId, chatId: targetChatId };
        const seededUserMessageId = `${CTA_SEED_USER_MESSAGE_ID_PREFIX}${Date.now()}`;
        ctaQueryClient.setQueryData(chatQueryKey(targetProjectId, targetChatId), (prev) =>
          appendStoryBibleCtaSeededUserMessage(prev as PersistentChatWithMessages | undefined, {
            targetChatId,
            seededUserMessageId,
            userMessage: payload.userMessage,
          }),
        );

        const isSynopsisCTA = payload.type === "generate_synopsis" || payload.type === "rewrite_synopsis";
        const isGenerateEntityCTA = payload.type === "generate_character" || payload.type === "generate_worldbuilding" || payload.type === "generate_outline" || payload.type === "rewrite_outline" || payload.type === "generate_cast";
        // For entity generation CTAs: don't set signalKey — the tool_start event activates
        // the signal naturally, and hardcoded cleanup in complete/error clears it
        const signalKey = isGenerateEntityCTA
          ? undefined
          : isSynopsisCTA
            ? getSynopsisCTASignal()
            : (payload.entryId && payload.traitId)
              ? getWorldbuildingTraitCTASignal(payload.entryId, payload.traitId)
              : (payload.characterId && payload.traitId)
                ? getTraitCTASignal(payload.characterId, payload.traitId)
                : payload.targetId ? getChapterCTASignal(payload.targetId) : undefined;

        usePersistentChatStore.getState().setCtaAction({
          type: payload.type as CTAActionType,
          status: "awaiting_input",
          targetSurface: payload.targetSurface,
          targetId: payload.targetId,
          targetName: payload.targetName,
          displayTitle: payload.displayTitle,
          userMessage: payload.userMessage,
          cannedResponse: payload.cannedResponse,
          signalKey,
          characterId: payload.characterId,
          entryId: payload.entryId,
          traitId: payload.traitId,
          traitLabel: payload.traitLabel,
          outlineFramework: payload.outlineFramework,
          customInstructions: payload.customInstructions,
        });

        if (signalKey) {
          useCrossAppSignalStore.getState().setSignalActive(signalKey, true);
        }

        // Dual-fire the legacy "Story Bible Generate" Mixpanel event so charts
        // built before the agentic chat beta gate was removed (when these CTAs
        // flowed through the legacy generation endpoints) keep working.
        publishStoryBibleGenerateForCTA(payload.type as CTAActionType);

        if (targetChatId !== chatId) {
          setChatId(targetChatId);
        }
      };

      if (isGenerateCastRetrigger) {
        usePersistentChatStore.getState().cancelActiveCta();
        queueMicrotask(startTriggeredCtaChat);
        return;
      }

      startTriggeredCtaChat();
    },
    { deps: [viewingProjectId, projectId, chatId, setChatId, ctaQueryClient] }
  );

  const loadOlderMessages = useCallback(async () => {
    if (queryChatId === NEW_CHAT_ID || isLoadingOlderMessagesRef.current) return;
    const pagination = data?.messagePagination;
    if (!pagination?.hasMoreOlder || !pagination.oldestLoadedMessageId) return;

    setIsLoadingOlderMessages(true);
    try {
      const olderPage = await fetchChatPage(queryChatId, { beforeMessageId: pagination.oldestLoadedMessageId });
      queryClient.setQueryData(chatQueryKey(projectId, queryChatId), (prev) =>
        mergeOlderChatMessages(prev as PersistentChatWithMessages | undefined, olderPage),
      );
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoadingOlderMessages(false);
    }
  }, [queryChatId, data?.messagePagination, projectId, queryClient]);

  const hasMoreOlderMessages = Boolean(data?.messagePagination?.hasMoreOlder);

  const contextValue = useMemo<PersistentChatContextType>(() => {
    const chatData = data ?? DefaultChatModel;
    const branchRawMessages = chatData.messages ?? [];
    const linearMessages = linearMessagesForDisplay(branchRawMessages as any, chatData.active_leaf_message_id, {
      inFlightUserMessageId,
    });
    return {
      ...chatData,
      messages: linearMessages as any,
      branchRawMessages,
      isLoading,
      isError,
      refetch,
      isFetching,
      chatId,
      setChatId,
      projectId,
      isStoryBibleActive,
      getSelection,
      editor,
      dismissActiveHighlightFromComposer,
      sendMessage: handleSendMessage,
      hasMoreOlderMessages,
      isLoadingOlderMessages,
      loadOlderMessages,
    };
  }, [
    chatId,
    data,
    dismissActiveHighlightFromComposer,
    editor,
    getSelection,
    handleSendMessage,
    hasMoreOlderMessages,
    isError,
    isFetching,
    isLoading,
    isLoadingOlderMessages,
    isStoryBibleActive,
    loadOlderMessages,
    projectId,
    refetch,
    setChatId,
    inFlightUserMessageId,
  ]);

  const isInitialLoad = isLoading && !data && !streaming;

  if (isInitialLoad) {
    return (
      <WithReset className="animate-in fade-in-0 pt-2 flex-1 flex-col items-center justify-center flex text-center h-full">
        <Text className='font-[var(--grey-4)]'>Loading this chat...</Text>
      </WithReset>
    );
  }

  if (isError) {
    return (
      <WithReset className="animate-in fade-in-0 flex-1 flex-col items-center justify-center flex text-center h-full">
        <Text className="text-lg font-medium">Something went wrong</Text>
        <Text className="text-center text-sm">We couldn't find your chat... Please refresh the page and try again, or contact our friendly support team!</Text>
      </WithReset>
    );
  }

  return <PersistentChatContext.Provider value={contextValue}>{children}</PersistentChatContext.Provider>;
};

export const usePersistentChat = () => {
  const context = useContext(PersistentChatContext);

  if (!context) {
    throw new Error("usePersistentChat must be used within a PersistentChatProvider");
  }

  return context;
};
